import { useIntl } from 'react-intl';
import type TrailSuitability from '@alltrails/trails/types/TrailSuitability';
import getTrailSuitabilityLabel from '@alltrails/trails/utils/getTrailSuitabilityLabel';
import type { ChipsFilterConfigV2, ToggleFilterConfigV2 } from '../../components/FilterControlV2/types';
import ExploreServiceFilters from '../../types/ExploreServiceFilters';

const accessibilityValues: TrailSuitability[] = ['ada', 'strollers', 'paved', 'partially-paved'];

type SuitabilityConfigV2 = {
  accessibilityChips: ChipsFilterConfigV2;
  dogToggle: ToggleFilterConfigV2;
  kidToggle: ToggleFilterConfigV2;
};

const useSuitabilityConfigV2 = (
  exploreServiceFilters: ExploreServiceFilters,
  onFiltersChange: (updatedFilters: ExploreServiceFilters) => void
): SuitabilityConfigV2 => {
  const intl = useIntl();
  const selected = exploreServiceFilters.suitability || [];

  const setSuitability = (next: TrailSuitability[]) => {
    const newFilters = { ...exploreServiceFilters };
    if (next.length === 0) {
      delete newFilters.suitability;
    } else {
      newFilters.suitability = next;
    }
    onFiltersChange(newFilters);
  };

  const setValue = (value: TrailSuitability, isSelected: boolean) => {
    const has = selected.includes(value);
    if (isSelected && !has) {
      setSuitability([...selected, value]);
    } else if (!isSelected && has) {
      setSuitability(selected.filter(current => current !== value));
    }
  };

  const dogToggle: ToggleFilterConfigV2 = {
    type: 'toggle',
    id: 'dogs',
    toggle: {
      labelText: getTrailSuitabilityLabel(intl, 'dogs'),
      labelTextVariant: 'text200bold',
      selected: selected.includes('dogs'),
      onChange: isSelected => setValue('dogs', isSelected)
    }
  };

  const kidToggle: ToggleFilterConfigV2 = {
    type: 'toggle',
    id: 'kids',
    toggle: {
      labelText: getTrailSuitabilityLabel(intl, 'kids'),
      labelTextVariant: 'text200bold',
      selected: selected.includes('kids'),
      onChange: isSelected => setValue('kids', isSelected)
    }
  };

  const chips = accessibilityValues.map(value => ({
    id: value,
    label: getTrailSuitabilityLabel(intl, value)
  }));

  const accessibilityChips: ChipsFilterConfigV2 = {
    type: 'chips',
    id: 'accessibility',
    selectionChips: {
      title: intl.formatMessage({ defaultMessage: 'Accessibility' }),
      isMultiSelect: true,
      chips,
      selectedChips: chips.filter(chip => selected.includes(chip.id)),
      onChange: chip => setValue(chip.id as TrailSuitability, !selected.includes(chip.id as TrailSuitability))
    }
  };

  return { accessibilityChips, dogToggle, kidToggle };
};

export default useSuitabilityConfigV2;
